import React from 'react';
import { sanitizePersonName } from 'utils/index';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import CustomSwitch from 'ui-components/CustomSwitch';
import {
  Building2,
  ChevronDown,
  FileText,
  Globe,
  Phone,
  UserRound,
} from 'lucide-react';
import whatsappIcon from 'assets/whatsapp_main_icon.png';
import { Primary } from 'ui-components/Buttons';
import CustomInputFieldOTP from 'ui-components/CustomInputField';
import CustomValidationText from 'ui-components/CustomValidationText';
import ImageDropzone from '../components/ImageDropzone';
import LocationFields from '../components/LocationFields';
import {
  BORDER,
  FieldLabel,
  FieldIcon,
  LinkedInIcon,
  PLACEHOLDER,
  SectionTitle,
  selectSx,
  TEXT_COLOR,
  TextInput,
} from '../primitives';

const titles = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'CA'];

// Names are capitalised as they are typed everywhere else in the app (Add Startup's
// startup/entity name, the old onboarding, Settings > Add User); onboarding v2 was
// the one flow that missed it. Only the first letter - the rest is left as typed -
// and a leading space is dropped so the capital actually lands on the first letter.
export const capitalizeFirst = (value) => {
  const text = String(value ?? '').trimStart();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const otpInputProps = {
  maxLength: 1,
  inputMode: 'numeric',
  pattern: '[0-9]*',
  style: { textAlign: 'center' },
  onInput: (event) => {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  },
};

const BasicDetails = ({
  isAngel,
  investorTypes,
  phoneCodes,
  whatsapp,
}) => {
  const formik = useFormikContext();
  const { values, errors, setFieldError } = formik;
  // validateOnChange is off, so an error set on "Next" would otherwise linger even
  // after the field is filled. Clear a field's own error the moment its value changes.
  const setFieldValue = (name, value, ...rest) => {
    formik.setFieldValue(name, value, ...rest);
    if (errors[name]) setFieldError(name, undefined);
  };

  // Figma shows "Mr." pre-selected. enableReinitialize replaces initialValues once
  // FetchProfileDetails lands, so pin the default here too — this sets the actual
  // formik value (not just the display), so the payload matches what's on screen.
  React.useEffect(() => {
    if (!titles.includes(values.title)) setFieldValue('title', 'Mr.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.title]);

  const stripHttps = (v) =>
    String(v ?? '').startsWith('https://') ? String(v).replace('https://', '') : v ?? '';

  return (
    <>
      {/* ------------------------------ Personal Details ------------------------------ */}
      <SectionTitle>Personal Details</SectionTitle>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <FieldLabel required>First Name</FieldLabel>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Select
              value={values.title || ''}
              displayEmpty
              onChange={(e) => setFieldValue('title', e.target.value)}
              variant="filled"
              disableUnderline
              IconComponent={(p) => <ChevronDown {...p} size={18} color="#667085" />}
              sx={{ ...selectSx, width: 'auto', minWidth: 92, flexShrink: 0 }}
            >
              {titles.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
            <TextInput
              icon={UserRound}
              name="first_name"
              placeholder="Enter first name"
              value={values.first_name}
              error={errors.first_name}
              onChange={(e) => setFieldValue('first_name', capitalizeFirst(sanitizePersonName(e.target.value)))}
            />
          </Box>
          {errors.title && <CustomValidationText value={errors.title} />}
        </Grid>

        <Grid item xs={12} md={6}>
          <FieldLabel required>Last Name</FieldLabel>
          <TextInput
            icon={UserRound}
            name="last_name"
            placeholder="Enter last name"
            value={values.last_name}
            error={errors.last_name}
            onChange={(e) => setFieldValue('last_name', capitalizeFirst(sanitizePersonName(e.target.value)))}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FieldLabel required>Select Investor type</FieldLabel>
          <Select
            value={values.investor_type || ''}
            displayEmpty
            onChange={(e) => setFieldValue('investor_type', e.target.value)}
            variant="filled"
            disableUnderline
            error={!!errors.investor_type}
            IconComponent={(p) => <ChevronDown {...p} size={18} color="#667085" />}
            renderValue={(selected) => {
              const match = (investorTypes || []).find((t) => t?.value === selected);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FieldIcon icon={UserRound} size={18} />
                  <Box
                    component="span"
                    sx={{ color: match ? TEXT_COLOR : PLACEHOLDER, fontSize: '0.95rem' }}
                  >
                    {match?.label || 'Select investor type'}
                  </Box>
                </Box>
              );
            }}
            sx={{
              ...selectSx,
              ...(errors.investor_type ? { borderColor: '#F04438' } : {}),
            }}
          >
            {(investorTypes || []).map((type) => (
              <MenuItem key={type?.value} value={type?.value}>
                {type?.label}
              </MenuItem>
            ))}
          </Select>
          {errors.investor_type && <CustomValidationText value={errors.investor_type} />}
        </Grid>

        <Grid item xs={12} md={6}>
          <FieldLabel optional>LinkedIn Profile URL</FieldLabel>
          <TextInput
            icon={LinkedInIcon}
            iconGradient={false}
            // The value is stored without the scheme (the old wizard strips it too),
            // so show it as a prefix rather than inviting the user to type it.
            prefix="https://"
            name="linkedIn_profile_url"
            placeholder="www.linkedin.com/in/yourprofile"
            value={stripHttps(values.linkedIn_profile_url)}
            error={errors.linkedIn_profile_url}
            onChange={(e) => setFieldValue('linkedIn_profile_url', stripHttps(e.target.value))}
          />
        </Grid>

        {/* Not in the Figma, but a shipped field of the old wizard — kept. */}
        <Grid item xs={12} md={6}>
          <FieldLabel optional>Date of Birth</FieldLabel>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextInput
              name="mm"
              placeholder="MM"
              inputProps={{ maxLength: 2, inputMode: 'numeric' }}
              value={values.mm}
              error={errors.mm}
              onChange={(e) => setFieldValue('mm', e.target.value.replace(/[^0-9]/g, ''))}
            />
            <TextInput
              name="dd"
              placeholder="DD"
              inputProps={{ maxLength: 2, inputMode: 'numeric' }}
              value={values.dd}
              error={errors.dd}
              onChange={(e) => setFieldValue('dd', e.target.value.replace(/[^0-9]/g, ''))}
            />
            <TextInput
              name="yyyy"
              placeholder="YYYY"
              inputProps={{ maxLength: 4, inputMode: 'numeric' }}
              value={values.yyyy}
              error={errors.yyyy}
              onChange={(e) => setFieldValue('yyyy', e.target.value.replace(/[^0-9]/g, ''))}
            />
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ my: 4, borderTop: `1px solid ${BORDER}` }} />

      {/* --------------------------- Organization / Profile --------------------------- */}
      <SectionTitle>{isAngel ? 'Investor Details' : 'Organization Details'}</SectionTitle>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2.5}>
            {!isAngel && (
              <Grid item xs={12}>
                <FieldLabel required>Organization Name</FieldLabel>
                <TextInput
                  icon={Building2}
                  name="organization_name"
                  placeholder="Enter organization name"
                  value={values.organization_name}
                  error={errors.organization_name}
                  onChange={(e) => setFieldValue('organization_name', capitalizeFirst(e.target.value))}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FieldLabel required>{isAngel ? 'Contact Number' : 'Office Contact Number'}</FieldLabel>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Select
                  value={values.country_code || ''}
                  onChange={(e) => {
                    const code = e.target.value;
                    setFieldValue('country_code', code);
                    // Indian mobile numbers are 10 digits — trim if switching to +91
                    if (code === '91') {
                      setFieldValue('phone_number', (values.phone_number || '').slice(0, 10));
                    }
                  }}
                  variant="filled"
                  disableUnderline
                  IconComponent={(p) => <ChevronDown {...p} size={18} color="#667085" />}
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <FieldIcon icon={Phone} size={18} />
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          color: TEXT_COLOR,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {/* strip any leading + in the data so we never render "++" */}
                        +{String(selected).replace(/^\+/, '')}
                      </Box>
                    </Box>
                  )}
                  sx={{ ...selectSx, width: 108, minWidth: 108, maxWidth: 108, flexShrink: 0 }}
                >
                  {(phoneCodes || []).map((c, i) => (
                    <MenuItem key={`${c?._id}-${i}`} value={String(c?.code)}>
                      +{String(c?.code).replace(/^\+/, '')} {c?.label}
                    </MenuItem>
                  ))}
                </Select>
                <TextInput
                  name="phone_number"
                  placeholder={isAngel ? 'Enter contact number' : 'Enter office contact number'}
                  // +91 (India) caps at 10 digits; other countries keep the 15-digit limit
                  inputProps={{ maxLength: values.country_code === '91' ? 10 : 15, inputMode: 'numeric' }}
                  value={values.phone_number}
                  error={errors.phone_number}
                  onChange={(e) =>
                    setFieldValue(
                      'phone_number',
                      e.target.value.replace(/[^0-9]/g, '').slice(0, values.country_code === '91' ? 10 : 15)
                    )
                  }
                />
              </Box>
            </Grid>

            {!isAngel && (
              <Grid item xs={12}>
                <FieldLabel optional>Website URL</FieldLabel>
                <TextInput
                  icon={Globe}
                  prefix="https://"
                  name="organization_website"
                  placeholder="www.yourwebsite.com"
                  value={stripHttps(values.organization_website)}
                  error={errors.organization_website}
                  onChange={(e) => setFieldValue('organization_website', stripHttps(e.target.value))}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FieldLabel optional>Bio</FieldLabel>
              <TextInput
                icon={FileText}
                name="bio"
                multiline
                minRows={2}
                placeholder={
                  isAngel
                    ? 'One liner like your linkedin headline.'
                    : "A brief overview of your organization's mission or tagline."
                }
                value={values.bio}
                error={errors.bio}
                onChange={(e) => setFieldValue('bio', e.target.value)}
                // fieldSx's `15px 14px` on .MuiFilledInput-input is right for a
                // single-line filled input, but wrong for a multiline one: MUI puts the
                // padding on the ROOT and expects the textarea to have padding: 0.
                // Leaving both applied double-insets the text away from the adornment.
                // Padding on the root, zero on the textarea, adornment at marginTop 0 —
                // then the icon and the first line of text share a top edge.
                extraSx={{
                  '& .MuiFilledInput-root': {
                    alignItems: 'flex-start',
                    padding: '15px 14px',
                  },
                  '& .MuiFilledInput-input': {
                    padding: 0,
                  },
                  '& .MuiInputAdornment-root': {
                    // Give the adornment the height of ONE text line and let its own
                    // align-items:center centre the icon within it, so the icon lines up
                    // with the first line of text instead of sitting flush to its top edge
                    // (which read as slightly too high). Uses line-height, not a magic pixel.
                    marginTop: '0 !important',
                    marginLeft: 0,
                    marginRight: '12px',
                    alignSelf: 'flex-start',
                    height: '1.4375em',
                    maxHeight: 'none',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <FieldLabel optional>
            {isAngel ? 'Upload Profile Photo' : 'Upload Organization Logo'}
          </FieldLabel>
          <ImageDropzone
            name={isAngel ? 'custom_image_url' : 'website_logo'}
            value={isAngel ? values.custom_image_url : values.website_logo}
            title={isAngel ? 'Crop Photo' : 'Crop Logo'}
            handleUpdateFile={(file) =>
              setFieldValue(isAngel ? 'custom_image_url' : 'website_logo', file)
            }
          />
        </Grid>
      </Grid>

      {/* ------------------------------- WhatsApp Notifications — commented out on /onboardingv2 per request -------------------------------
          Optional, non-blocking WhatsApp opt-in + OTP block kept from the old wizard's
          WhatsAppNotifications step. Markup and logic left intact below so it can be restored
          verbatim; only its leading divider lives inside this comment, so one divider still
          separates Organization Details from Geography.

      <Box sx={{ my: 4, borderTop: `1px solid ${BORDER}` }} />

      <SectionTitle>WhatsApp Notifications</SectionTitle>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: 1,
          px: 2,
          height: 56,
          borderRadius: '12px',
          border: `1px solid ${BORDER}`,
          background: '#fff',
        }}
      >
        <img src={whatsappIcon} alt="WhatsApp" width={20} height={20} />
        <Typography sx={{ fontSize: '0.95rem', color: TEXT_COLOR }}>
          Enable WhatsApp notification
        </Typography>
        <CustomSwitch
          checked={!!values.enabled}
          onChange={(e) => {
            setFieldValue('enabled', e.target.checked);
            if (!e.target.checked) whatsapp.reset();
          }}
        />
      </Box>

      {values.enabled && (
        <Grid container spacing={2.5} sx={{ mt: 0 }}>
          <Grid item xs={12} md={8}>
            <FieldLabel optional>Mobile Number</FieldLabel>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Select
                value={values.phone_code || ''}
                onChange={(e) => setFieldValue('phone_code', e.target.value)}
                variant="filled"
                disableUnderline
                IconComponent={(p) => <ChevronDown {...p} size={18} color="#667085" />}
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FieldIcon icon={Phone} size={18} />
                    <Box component="span" sx={{ color: TEXT_COLOR, fontWeight: 500 }}>
                      +{selected}
                    </Box>
                  </Box>
                )}
                sx={{ ...selectSx, width: 'auto', minWidth: 108, flexShrink: 0 }}
              >
                {(phoneCodes || []).map((c, i) => (
                  <MenuItem key={`wa-${c?._id}-${i}`} value={String(c?.code)}>
                    +{c?.code} {c?.label}
                  </MenuItem>
                ))}
              </Select>
              <TextInput
                name="whatsapp_number"
                placeholder="Enter mobile number"
                inputProps={{ maxLength: 15, inputMode: 'numeric' }}
                value={values.whatsapp_number}
                disabled={!!values.otp_verified}
                onChange={(e) => {
                  setFieldValue('whatsapp_number', e.target.value.replace(/[^0-9]/g, ''));
                  setFieldValue('otp_verified', false);
                  whatsapp.reset();
                }}
                extraSx={
                  values.otp_verified
                    ? { '& .MuiFilledInput-root': { borderColor: '#12B76A' } }
                    : {}
                }
                InputProps={{
                  disableUnderline: true,
                  endAdornment: values.otp_verified ? (
                    <InputAdornment position="end" sx={{ mr: 1.5 }}>
                      <Typography sx={{ color: '#12B76A', fontSize: '0.85rem', fontWeight: 600 }}>
                        Verified
                      </Typography>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
          </Grid>

          {!values.otp_verified && (
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Primary
                onClick={whatsapp.sendOTP}
                disabled={whatsapp.isSending || !values.whatsapp_number}
                sx={{ py: 1.6, minWidth: 120 }}
              >
                {whatsapp.otpSent ? 'Resend OTP' : 'Verify'}
              </Primary>
            </Grid>
          )}

          {whatsapp.otpSent && !values.otp_verified && (
            <Grid item xs={12}>
              <FieldLabel>Enter OTP</FieldLabel>
              <Box sx={{ display: 'flex', gap: 2, maxWidth: 320 }}>
                {whatsapp.inputRefs.map((ref, index) => (
                  <CustomInputFieldOTP
                    key={index}
                    inputProps={otpInputProps}
                    sx={{ marginBottom: 0 }}
                    inputRef={ref}
                    onChange={(event) => whatsapp.handleOtpChange(event, index)}
                    onKeyDown={(event) => whatsapp.handleOtpKeyDown(event, index)}
                    onPaste={index === 0 ? whatsapp.handleOtpPaste : undefined}
                  />
                ))}
              </Box>
              {whatsapp.error && <CustomValidationText value={whatsapp.error} />}
              <Primary
                onClick={whatsapp.verifyOTP}
                disabled={whatsapp.isVerifying}
                sx={{ mt: 2, py: 1.4, minWidth: 120 }}
              >
                Verify OTP
              </Primary>
            </Grid>
          )}
        </Grid>
      )}
      ------------------------------- end WhatsApp Notifications (commented out) ------------------------------- */}

      <Box sx={{ my: 4, borderTop: `1px solid ${BORDER}` }} />

      {/* ---------------------------------- Geography ---------------------------------- */}
      <SectionTitle>Geography</SectionTitle>

      {/* One searchable Location field, same as the startup app; manual cascade behind a link. */}
      <Grid container spacing={2.5}>
        <LocationFields formik={formik} />
      </Grid>
    </>
  );
};

export default BasicDetails;
